/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Toggle, Size, Icon, Modal } from '@vaisala-rockhopper/components';
import './CalculatorCardRow.scss';
import IconButton from '../navigation/IconButton';
import { VaiIcon } from '@vaisala-rockhopper/design-tokens';
import EnhancedInput from '../inputs/EnhancedInput';
import ColorKey, { ColorKeyType } from './ColorKey';
import { CalculatorResult, ParameterModifier, ParameterName } from '../../../modules/calculator/calculatorTypes';
import { paramNameForRow } from '../../../modules/calculator/configParser';
import UnitController from '../../../modules/units/UnitController';
import EnhancedRadioGroup from '../inputs/EnhancedRadioGroup';

export type RowType = 'numeric' | 'time' | 'toggle' | 'plain' | 'info';

interface CalculatorCardRowProps {
  config: any;
  cardIndex: number;
  rowType: RowType;
  manualUnit: boolean;
  onParameterChange: (parameterName: ParameterName, value: ParameterModifier, phase?: number) => void;
  variant?: string;
  currentResult?: CalculatorResult;
  theme: string;
  activeMethod: number;
}

const CalculatorCardRow = ({
  config,
  cardIndex,
  rowType,
  manualUnit,
  onParameterChange,
  variant,
  currentResult,
  theme,
  activeMethod,
}: CalculatorCardRowProps) => {
  const { t } = useTranslation();
  const quantity = config.quantity;
  const [toggleOn, setToggleOn] = React.useState<boolean>(false);
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [currentUnit, setCurrentUnit] = React.useState<string>(
    rowType === 'toggle' ? '' : UnitController.formatUnit(UnitController.unitForQuantity(quantity))
  );
  const unitOptions = UnitController.allUnitsForQuantity(quantity);
  //const defaultUnit = unitOptions ? unitOptions[0] : undefined;
  const min = config.min;
  const max = config.max;
  const valuePrecision = 6;
  type timeElement = 0 | 1 | 2;

  React.useEffect(() => {
    const result: any = currentResult?.calculatorData?.cards[cardIndex];
    if (result?.dehumidification === true) {
      setToggleOn(true);
    } else {
      setToggleOn(false);
    }
    if (variant === 'graph') {
      setToggleOn(false);
    }
  }, [currentResult, cardIndex, variant]);

  const paramName = paramNameForRow(config);

  const convertMinutesToHMS = (sec: number) => {
    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec - hours * 3600) / 60);
    const seconds = sec - hours * 3600 - minutes * 60;
    return [hours, minutes, seconds];
  };

  const getCurrentTime = React.useCallback(() => {
    if (currentResult && config) {
      const card = currentResult?.calculatorData?.cards[cardIndex] as any;
      let val;
      if (card) {
        val = card[`${paramName}${config.phase === 0 || config.phase >= 1 ? config.phase : ''}`];
      } else {
        val = config.default;
      }
      if (val) {
        val = convertMinutesToHMS(val);
        setTimeElements(val);
      } else {
        setTimeElements([0, 0, 0]);
      }
    } else {
      setTimeElements([0, 0, 0]);
    }
  }, [cardIndex, config, currentResult, paramName]);

  const getCurrent = () => {
    if (currentResult && config) {
      const card = currentResult?.calculatorData?.cards[cardIndex] as any;
      let val;
      if (card) {
        val = card[`${paramName}${config.phase === 0 || config.phase >= 1 ? config.phase : ''}`];
      } else {
        val = config.default;
      }

      return val;
    }

    // TODO: convert unit when configured
    //console.log(UnitController.convert(quantity, val, defaultUnit, currentUnit));
  };

  const getCurrentTrimmed = (precision: number) => {
    const val = getCurrent() as number;
    if (val === 0) {
      return 0;
    } else if (isNaN(val)) {
      return '--';
    } else if (!val) {
      return '--';
    } else if (val === Infinity) {
      return '∞';
    }
    const fval = parseFloat(`${val}`);
    return Math.abs(val) > 1000000 ? Math.round(fval / 1000) + 'k' : fval.toFixed(Math.max(precision - `${Math.round(val)}`.length, 0));
  };

  const [timeElements, setTimeElements] = React.useState([0, 0, 0]);
  React.useEffect(() => {
    getCurrentTime();
  }, [currentResult, config, getCurrentTime]);

  const onValueChange = (value: number | boolean) => {
    if (value === true) {
      setToggleOn(true);
    } else if (value === false) {
      setToggleOn(false);
    }
    onParameterChange(paramName, value, config.phase);
  };

  const onTimeElementChange = (element: timeElement, val: number) => {
    const elements = [...timeElements];
    elements[element] = val;
    setTimeElements(elements);

    const seconds = elements[0] * 60 * 60 + elements[1] * 60 + elements[2];
    onValueChange(seconds);
  };

  const renderTimeInput = (element: timeElement, max: number, separator: boolean) => (
    <>
      <div className="input-wrap">
        <EnhancedInput
          timeInput
          value={timeElements[element]}
          onDelayedChange={(val) => onTimeElementChange(element, val)}
          step={1}
          min={0}
          max={max}
        />
      </div>
      {separator && <div className="time-input-separator">:</div>}
    </>
  );

  const colorKeyVariant = (): ColorKeyType => {
    switch (config.labelKey) {
      case 'param.relativeHumidity':
        return 'rh';
      case 'param.relativeSaturation':
        return 'rs';
      case 'param.h2o2Concentration':
      case 'param.VH2O2Concentration':
        return 'ppm';
    }
    return 'off';
  };

  const tabIndex = config?.advanced === true ? (toggleOn ? 0 : -1) : 0;

  return (
    <>
      <div
        className={`calculator-card-row ${rowType} ${manualUnit ? 'calculator-card-row--manual-unit' : ''}   ${
          config?.advanced === true ? (toggleOn ? 'toggle-on' : 'toggle-off') : ''
        }`}
      >
        <div className="calculator-card-row__wrap">
          <div className="block block-color-key">
            <ColorKey variant={colorKeyVariant()} />
          </div>

          <div className="block block-title">
            <span>
              <span dangerouslySetInnerHTML={{ __html: t(config.labelKey) }} />
              {config.phase !== undefined && activeMethod === 0 && <sub>{` ${config.phase}`}</sub>}
            </span>
          </div>
        </div>
        <div className="calculator-card-row__wrap">
          <div className="block block-value">
            {rowType === 'numeric' && (
              <EnhancedInput
                width={Size.XS}
                value={getCurrent()}
                onDelayedChange={(val) => onValueChange(val)}
                step={Math.pow(10, -(config.precision ?? 0))}
                min={min}
                max={max}
                tabIndex={tabIndex}
              />
            )}
            {rowType === 'time' && (
              <div className="time-input">
                {renderTimeInput(0, 99, true)}
                {renderTimeInput(1, 59, true)}
                {renderTimeInput(2, 59, false)}
              </div>
            )}
            {(rowType === 'plain' || rowType === 'info') && getCurrentTrimmed(valuePrecision)}
          </div>

          {rowType !== 'toggle' && (
            <>
              <div className="block block-unit">
                {manualUnit && unitOptions && unitOptions.length > 1 ? (
                  <IconButton className="manual-unit-button" buttonSize={Size.M} onClick={() => setModalOpen(true)}>
                    {' '}
                    <span dangerouslySetInnerHTML={{ __html: currentUnit }} />
                    <Icon name={VaiIcon.ChevronRight} />
                  </IconButton>
                ) : (
                  <span dangerouslySetInnerHTML={{ __html: currentUnit }} />
                )}
              </div>
            </>
          )}
          {rowType === 'toggle' && (
            <>
              <div className="block block-unit">
                <Toggle text="" checked={toggleOn ? true : false} onChange={onValueChange} tabIndex={tabIndex} />
              </div>
            </>
          )}
        </div>
      </div>
      {unitOptions && unitOptions.length > 1 && (
        <Modal
          className={`modal ${theme === 'vai-theme-dark' ? 'modal--vai-theme-dark' : ''}`}
          isOpen={modalOpen}
          showCloseIcon
          width={Size.S}
          onDismiss={() => setModalOpen(false)}
        >
          <EnhancedRadioGroup
            options={unitOptions}
            value={currentUnit}
            onChange={(val) => {
              setModalOpen(false);
              setCurrentUnit(val);
            }}
          />
        </Modal>
      )}
    </>
  );
};

export default CalculatorCardRow;
